import React, { useEffect } from 'react';
import { Icon } from '../../common/commonModels';
import { Document } from '../../models/document';
import TileComponent from '../../common/components/TileComponent';
import DocumentTileContainer from '../../documents/DocumentTileContainer';
import { Truck } from '../../models/truck';
import { FlexBox, AlignItems, FlexGrow, FlexWrap } from '../../utils/css-classes';
import { useModal } from '../../contexts/modal-context';
import { PalletBoxShipmentsModal } from '../../components/pallet-boxes/PalletBoxShipmentsModal';
import { palletBoxName } from '../../components/pallet-boxes/PalletBoxName';

interface Props {
  truck: Truck;
  highlight?: string;
  matchType?: number;
  saveDocument: (doc: Document) => void;
}

export enum MatchType {
  NO_MATCH         = 0b000,
  BORDER_DOCUMENT  = 0b001,
  CARRIER_DOCUMENT = 0b010,
  PALLET_BOX       = 0b100,
}

// tslint:disable-next-line:function-name
export function TruckManifestComponentV2({ truck, saveDocument, highlight, matchType }: Props) {
  const modal = useModal();

  const [drawer, setDrawer] = React.useState<'border' | 'carrier' | 'shipment' | null>(null);
  const [borderDocuments, setBorderDocuments] = React.useState(0);
  const [carrierDocuments, setCarrierDocuments] = React.useState(0);
  const [palletBoxCount, setPalletBoxCount] = React.useState(0);

  useEffect(
    () => {
      let borderDocumentsCount = 0;
      let carrierDocumentsCount = 0;
      if (
        truck.manifest &&
        truck.manifest.documents
      ) {
        borderDocumentsCount = truck.manifest.documents.filter(document => document.carrierAccountKey === null).length;
        carrierDocumentsCount = truck.manifest.documents.filter(document => document.carrierAccountKey !== null).length;
      }
      setPalletBoxCount(truck.palletBoxes.reduce(
        (acc, cur) =>
          acc + Number(!(truck.locationKey.indexOf('windsor') === 0 && cur.createdBy === 'sop-backend')),
        0,
      ));
      setBorderDocuments(borderDocumentsCount);
      setCarrierDocuments(carrierDocumentsCount);
    },
    [truck],
  );

  useEffect(
    () => {
      if (matchType && matchType > 0) {
        if (matchType & MatchType.PALLET_BOX) {
          setDrawer('shipment');
          return;
        }
        if (matchType & MatchType.BORDER_DOCUMENT) {
          setDrawer('border');
          return;
        }
        if (matchType & MatchType.CARRIER_DOCUMENT) {
          setDrawer('carrier');
          return;
        }
      }

      if (!matchType && !highlight) {
        setDrawer(borderDocuments ? 'border' : carrierDocuments ? 'carrier' : null);
      }
    },
    [matchType, highlight, borderDocuments, carrierDocuments],
  );

  return (
    <div className={FlexGrow.GROW_1}>
      <div className={`${FlexBox.FLEX} ${AlignItems.CENTER}`}>
        {borderDocuments > 0 &&
          <TileComponent
            title="Border documents"
            iconClassName={drawer === 'border' ? Icon.ActiveCategoryBorder : Icon.CategoryBorder}
            onClick={() => setDrawer(drawer === 'border' ? null : 'border')}
            paddingClass="pt-4 pb-2"
            topRightLabelText={borderDocuments.toString()}
          />
        }
        {carrierDocuments > 0 &&
          <TileComponent
            title="Carrier documents"
            iconClassName={drawer === 'carrier' ? Icon.ActiveCategoryCarrier : Icon.CategoryCarrier}
            onClick={() => setDrawer(drawer === 'carrier' ? null : 'carrier')}
            paddingClass="pt-4 pb-2"
            topRightLabelText={carrierDocuments.toString()}
          />
        }
        <TileComponent
          title="Pallet boxes"
          iconClassName={drawer === 'shipment' ? Icon.ActiveCategoryShipment : Icon.CategoryShipment}
          onClick={() => setDrawer(drawer === 'shipment' ? null : 'shipment')}
          paddingClass="pt-4 pb-2"
          topRightLabelText={palletBoxCount.toString()}
        />
      </div>
      {drawer &&
        <div className={`divider-h ${FlexBox.FLEX} ${FlexWrap.WRAP}`}>
          {
            drawer === 'border' &&
              truck.manifest &&
              truck.manifest!.documents &&
              truck.manifest!.documents
                .filter(document => document.carrierAccountKey === null)
                .map(document => (
                  <DocumentTileContainer
                    key={document.id}
                    truck={truck}
                    document={document}
                    iconClassName={Icon.BorderDocument}
                    onClick={() => saveDocument(document)}
                    highlight={highlight}
                  />
                ))
          }
          {
            drawer === 'carrier' &&
              truck.manifest &&
              truck.manifest!.documents &&
              truck.manifest!.documents
                .filter(document => document.carrierAccountKey !== null)
                .map(document => (
                  <DocumentTileContainer
                    key={document.id}
                    truck={truck}
                    document={document}
                    iconClassName={Icon.Document}
                    onClick={() => saveDocument(document)}
                    highlight={highlight}
                  />
                ))
          }
          {
            drawer === 'shipment' &&
              truck.palletBoxes
                .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
                .map(palletBox => <TileComponent
                                    key={palletBox.externalId}
                                    title={palletBoxName(palletBox)}
                                    iconClassName={Icon.Closed}
                                    topRightLabelText={palletBox.shipmentCount.toString()}
                                    highlight={highlight}
                                    onClick={() => modal(
                                      palletBoxName(palletBox),
                                      () => <PalletBoxShipmentsModal palletBox={palletBox}/>,
                                      {
                                        bsStyle: 'info',
                                        bsSize: 'lg',
                                        style: {
                                          width: '80%',
                                        },
                                      },
                                    )}
                                  />)
          }
        </div>
      }
    </div>
  );
}
